import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { createCategory, deleteCategory, getCategories, updateCategory } from '../../services/apiService';

const Categories = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [notification, setNotification] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 12;
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768;
  });

  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return items;
    return items.filter((c) => String(c?.name || '').toLowerCase().includes(q));
  }, [items, searchTerm]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE)), [filtered.length]);

  const paginated = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filtered.slice(start, start + ITEMS_PER_PAGE);
  }, [filtered, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, items.length]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    const email = localStorage.getItem('userEmail');
    if (!isLoggedIn || !email) {
      navigate('/login');
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getCategories();
      const sorted = [...(data || [])].sort((a, b) => {
        const da = a?.created_at ? new Date(a.created_at).getTime() : 0;
        const db = b?.created_at ? new Date(b.created_at).getTime() : 0;
        // newest first
        if (db !== da) return db - da;
        // fallback stable ordering by name
        return String(a?.name || '').localeCompare(String(b?.name || ''));
      });
      setItems(sorted);
    } catch (e) {
      setError(e.message || 'Error loading categories');
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const onCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    try {
      await createCategory({ name });
      setNewName('');
      await load();
      showNotification('Category created successfully', 'success');
      setShowCreateModal(false);
    } catch (e) {
      showNotification(e.message || 'Error creating category', 'error');
    } finally {
      setSaving(false);
    }
  };

  const onStartEdit = (c) => {
    setEditingId(c.id);
    setEditingName(c.name || '');
    setShowEditModal(true);
  };

  const onCancelEdit = () => {
    setEditingId(null);
    setEditingName('');
    setShowEditModal(false);
  };

  const onSaveEdit = async () => {
    const name = editingName.trim();
    if (!editingId || !name) return;
    setSaving(true);
    try {
      await updateCategory(editingId, { name });
      setEditingId(null);
      setEditingName('');
      setShowEditModal(false);
      await load();
      showNotification('Category updated successfully', 'success');
    } catch (e) {
      showNotification(e.message || 'Error updating category', 'error');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = (c) => {
    setCategoryToDelete(c);
  };

  const confirmDeleteCategory = async () => {
    if (!categoryToDelete) return;
    setSaving(true);
    try {
      await deleteCategory(categoryToDelete.id);
      setCategoryToDelete(null);
      await load();
      showNotification('Category deleted successfully', 'success');
    } catch (e) {
      showNotification(e.message || 'Error deleting category', 'error');
    } finally {
      setSaving(false);
    }
  };

  // copyToClipboard removed (slug hidden in UI)

  const goToPage = (p) => {
    const next = Math.min(Math.max(1, p), totalPages);
    setCurrentPage(next);
  };

  return (
    <AdminLayout>
      <div style={{ padding: isMobile ? '16px' : '24px' }}>
        {/* Notification Toast */}
        {notification && (
          <div
            style={{
              position: 'fixed',
              top: isMobile ? '12px' : '20px',
              left: isMobile ? '12px' : 'auto',
              right: isMobile ? '12px' : '20px',
              zIndex: 10001,
              minWidth: isMobile ? 'auto' : '300px',
              maxWidth: isMobile ? 'calc(100vw - 24px)' : '500px',
              background: notification.type === 'success' ? '#10b981' : '#ef4444',
              color: 'white',
              padding: '16px 20px',
              borderRadius: '12px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              animation: 'slideInRight 0.3s ease-out',
              cursor: 'pointer'
            }}
            onClick={() => setNotification(null)}
          >
            <style>{`
              @keyframes slideInRight {
                from { transform: translateX(100%); opacity: 0; }
                to { transform: translateX(0); opacity: 1; }
              }
            `}</style>
            <div style={{ fontSize: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '24px' }}>
              {notification.type === 'success' ? <i className="fas fa-check-circle" /> : <i className="fas fa-exclamation-circle" />}
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, fontSize: '14px', fontWeight: 500, lineHeight: '1.5' }}>
                {notification.message}
              </p>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setNotification(null);
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                opacity: 0.8,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.8'; }}
            >
              <i className="fas fa-times" />
            </button>
          </div>
        )}

        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #96ca2d 0%, #7fb800 100%)',
          borderRadius: '16px',
          padding: isMobile ? '18px' : '24px',
          marginBottom: '24px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: '-20%',
            right: '-10%',
            width: '200px',
            height: '200px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
            zIndex: 1
          }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 2, flexWrap: 'wrap', gap: isMobile ? '12px' : '20px' }}>
            <div style={{ flex: 1 }}>
              <h1 style={{ margin: 0, fontSize: isMobile ? '22px' : '28px', fontWeight: 700 }}>Categories</h1>
              <p style={{ margin: '8px 0 0 0', opacity: 0.95 }}>Manage categories used by News</p>

              {/* Stats */}
              <div style={{
                display: 'flex',
                gap: '16px',
                marginTop: '24px',
                flexWrap: 'wrap'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  padding: '12px 20px',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  width: isMobile ? '100%' : 'auto'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.25)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    color: 'white'
                  }}>
                    <i className="fas fa-tags" />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ fontSize: '28px', fontWeight: 700, color: 'white', lineHeight: '1.2' }}>
                      {items.length}
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px', fontWeight: 500, marginTop: '2px' }}>
                      Total categories
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Create + Search */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '24px',
          border: '1px solid rgba(150, 202, 45, 0.1)',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: isMobile ? 'flex-start' : 'space-between',
          gap: '16px',
          alignItems: isMobile ? 'stretch' : 'center'
        }}>
          {/* Left (desktop): Search + Reset */}
          <div style={{
            width: isMobile ? '100%' : 'auto',
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
            justifyContent: isMobile ? 'stretch' : 'flex-start',
            flexWrap: 'wrap',
            flex: isMobile ? 'unset' : 1,
            minWidth: 0
          }}>
            <div style={{ flex: 1, minWidth: 0, width: isMobile ? '100%' : 'auto', maxWidth: isMobile ? '100%' : '520px' }}>
              <div style={{ position: 'relative' }}>
                <i className="fas fa-search" style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9ca3af',
                  fontSize: '14px'
                }} />
                <input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search categories..."
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    padding: '10px 12px 10px 38px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'all 0.2s',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#96ca2d';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                  }}
                />
              </div>
            </div>

            {searchTerm.trim() && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  padding: '10px 16px',
                  background: '#f3f4f6',
                  border: '1px solid #e5e7eb',
                  borderRadius: '10px',
                  color: '#6b7280',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                <i className="fas fa-times" style={{ fontSize: '12px' }} />
                Reset
              </button>
            )}
          </div>

          {/* Right (desktop): Add */}
          <div style={{ width: isMobile ? '100%' : 'auto', flexShrink: 0 }}>
            <button
              onClick={() => {
                setNewName('');
                setShowCreateModal(true);
              }}
              disabled={saving}
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #96ca2d 0%, #7ba818 100%)',
                border: 'none',
                borderRadius: '10px',
                color: 'white',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: 700,
                transition: 'all 0.2s',
                boxShadow: '0 4px 12px rgba(150, 202, 45, 0.25)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: isMobile ? '100%' : 'auto',
                justifyContent: 'center'
              }}
            >
              <i className="fas fa-plus-circle" />
              Add Category
            </button>
          </div>
        </div>

        {isLoading ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '16px'
          }}>
            {[...Array(10)].map((_, idx) => (
              <div key={idx} style={{
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '16px',
                padding: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
              }}>
                <div style={{ height: 12, width: '55%', background: '#eef2f7', borderRadius: 6, marginBottom: 10 }} />
                <div style={{ height: 10, width: '35%', background: '#f3f4f6', borderRadius: 6, marginBottom: 14 }} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ height: 34, width: 86, background: '#eef2f7', borderRadius: 10 }} />
                  <div style={{ height: 34, width: 86, background: '#eef2f7', borderRadius: 10 }} />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div style={{
            background: '#fff',
            border: '1px solid #fecaca',
            borderRadius: '16px',
            padding: '18px',
            color: '#b91c1c'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <i className="fas fa-exclamation-triangle" />
              <div style={{ fontWeight: 900 }}>Error</div>
            </div>
            <div style={{ marginTop: 8, color: '#7f1d1d' }}>{error}</div>
            <button
              onClick={load}
              style={{
                marginTop: 14,
                padding: '10px 14px',
                background: '#fff',
                border: '1px solid #fecaca',
                borderRadius: '10px',
                cursor: 'pointer',
                fontWeight: 900,
                color: '#b91c1c',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              <i className="fas fa-redo" />
              Retry
            </button>
          </div>
        ) : (
          <>
            {filtered.length === 0 ? (
              <div style={{
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '16px',
                padding: '40px 16px',
                color: '#6b7280',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.7 }}>
                  <i className="fas fa-tags" />
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 4 }}>
                  {searchTerm.trim() ? 'No results' : 'No categories yet'}
                </div>
                <div style={{ fontSize: 13 }}>
                  {searchTerm.trim() ? 'Try a different search.' : 'Click "Add Category" to create one.'}
                </div>
              </div>
            ) : (
              <>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(260px, 1fr))',
                  gap: '16px'
                }}>
                  {paginated.map((c) => (
                    <div
                      key={c.id}
                      style={{
                        background: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '16px',
                        padding: '16px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 12
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontWeight: 900,
                            color: '#111827',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: 16
                          }}>
                            {c.name}
                          </div>
                          {/* slug hidden by design */}
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => onStartEdit(c)}
                          disabled={saving}
                          style={{
                            padding: '10px 12px',
                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                            border: 'none',
                            boxShadow: '0 4px 12px rgba(245, 158, 11, 0.25)',
                            color: '#fff',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            fontWeight: 900
                          }}
                        >
                          <i className="fas fa-edit" />
                          Edit
                        </button>
                        <button
                          onClick={() => onDelete(c)}
                          disabled={saving}
                          style={{
                            padding: '10px 12px',
                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            fontWeight: 900,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            boxShadow: '0 4px 12px rgba(239, 68, 68, 0.25)'
                          }}
                        >
                          <i className="fas fa-trash" />
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination (same style as Admin News) */}
                {filtered.length > ITEMS_PER_PAGE && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 0,
                    marginTop: '22px',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{ display: 'inline-flex', gap: 0 }}>
                      <button
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        style={{
                          padding: '8px 10px',
                          minWidth: 36,
                          borderRadius: '6px 0 0 6px',
                          border: '1px solid #96ca2d',
                          background: currentPage === 1 ? '#f2f2f2' : '#96ca2d',
                          color: currentPage === 1 ? '#888' : '#fff',
                          cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                          margin: 0,
                          borderRightWidth: 0
                        }}
                      >
                        &lt;
                      </button>

                      {(() => {
                        const buttons = [];
                        const renderBtn = (i, isFirst = false) => (
                          <button
                            key={i}
                            onClick={() => goToPage(i)}
                            style={{
                              padding: '8px 10px',
                              minWidth: 36,
                              borderRadius: 0,
                              border: `1px solid ${currentPage === i ? '#96ca2d' : '#ddd'}`,
                              background: currentPage === i ? '#96ca2d' : '#fff',
                              color: currentPage === i ? '#fff' : '#333',
                              cursor: 'pointer',
                              margin: 0,
                              borderLeftWidth: isFirst ? '1px' : 0
                            }}
                          >
                            {i}
                          </button>
                        );

                        const shown = Math.min(totalPages, 3);
                        for (let i = 1; i <= shown; i++) {
                          buttons.push(renderBtn(i, i === 1));
                        }
                        if (totalPages > 3) {
                          buttons.push(
                            <span key="ell" style={{ padding: '8px 6px', border: '1px solid #ddd', borderLeftWidth: 0 }}>...</span>
                          );
                        }
                        return buttons;
                      })()}

                      <button
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        style={{
                          padding: '8px 10px',
                          minWidth: 36,
                          borderRadius: '0 6px 6px 0',
                          border: '1px solid #96ca2d',
                          background: currentPage === totalPages ? '#f2f2f2' : '#96ca2d',
                          color: currentPage === totalPages ? '#888' : '#fff',
                          cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                          margin: 0,
                          borderLeftWidth: 0
                        }}
                      >
                        &gt;
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.45)',
              zIndex: 10000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                maxWidth: 520,
                background: 'white',
                borderRadius: 18,
                border: '1px solid rgba(0,0,0,0.08)',
                boxShadow: '0 24px 80px rgba(0,0,0,0.25)',
                overflow: 'hidden'
              }}
            >
              <div style={{
                padding: '18px 18px',
                background: 'linear-gradient(135deg, #96ca2d 0%, #7fb800 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 40,
                    height: 40,
                    borderRadius: 12,
                    background: 'rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <i className="fas fa-plus" />
                  </div>
                  <div>
                    <div style={{ fontWeight: 900, fontSize: 16 }}>Add Category</div>
                    <div style={{ fontSize: 13, opacity: 0.9 }}>Create a new category for News</div>
                  </div>
                </div>
                <button
                  onClick={() => setShowCreateModal(false)}
                  disabled={saving}
                  style={{
                    background: 'rgba(255,255,255,0.15)',
                    border: '1px solid rgba(255,255,255,0.25)',
                    color: 'white',
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    cursor: saving ? 'not-allowed' : 'pointer'
                  }}
                >
                  <i className="fas fa-times" />
                </button>
              </div>

              <div style={{ padding: 18 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 800, color: '#374151', marginBottom: 8 }}>
                  Category name
                </label>
                <input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. Innovation"
                  style={{
                    width: '100%',
                    padding: '12px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    outline: 'none',
                    fontSize: 14
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onCreate();
                    if (e.key === 'Escape') setShowCreateModal(false);
                  }}
                  autoFocus
                />
              </div>

              <div style={{
                padding: 18,
                borderTop: '1px solid #e5e7eb',
                background: '#f9fafb',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 10,
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={() => setShowCreateModal(false)}
                  disabled={saving}
                  style={{
                    padding: '10px 14px',
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontWeight: 900,
                    color: '#6b7280'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={onCreate}
                  disabled={saving || !newName.trim()}
                  style={{
                    padding: '10px 14px',
                    background: 'linear-gradient(135deg, #96ca2d 0%, #7ba818 100%)',
                    border: 'none',
                    borderRadius: 12,
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontWeight: 900,
                    color: 'white',
                    boxShadow: '0 6px 16px rgba(150, 202, 45, 0.25)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                >
                  <i className="fas fa-check" />
                  Create
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.45)',
              zIndex: 10000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                maxWidth: 520,
                background: 'white',
                borderRadius: 18,
                border: '1px solid rgba(0,0,0,0.08)',
                boxShadow: '0 24px 80px rgba(0,0,0,0.25)',
                overflow: 'hidden'
              }}
            >
              <div style={{
                padding: '18px 18px',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 40,
                    height: 40,
                    borderRadius: 12,
                    background: 'rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <i className="fas fa-edit" />
                  </div>
                  <div>
                    <div style={{ fontWeight: 900, fontSize: 16 }}>Edit Category</div>
                    <div style={{ fontSize: 13, opacity: 0.9 }}>Update category name</div>
                  </div>
                </div>
                <button
                  onClick={onCancelEdit}
                  disabled={saving}
                  style={{
                    background: 'rgba(255,255,255,0.15)',
                    border: '1px solid rgba(255,255,255,0.25)',
                    color: 'white',
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    cursor: saving ? 'not-allowed' : 'pointer'
                  }}
                >
                  <i className="fas fa-times" />
                </button>
              </div>

              <div style={{ padding: 18 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 800, color: '#374151', marginBottom: 8 }}>
                  Category name
                </label>
                <input
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  placeholder="e.g. Innovation"
                  style={{
                    width: '100%',
                    padding: '12px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    outline: 'none',
                    fontSize: 14
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onSaveEdit();
                    if (e.key === 'Escape') onCancelEdit();
                  }}
                  autoFocus
                />
              </div>

              <div style={{
                padding: 18,
                borderTop: '1px solid #e5e7eb',
                background: '#f9fafb',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 10,
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={onCancelEdit}
                  disabled={saving}
                  style={{
                    padding: '10px 14px',
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontWeight: 900,
                    color: '#6b7280'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={onSaveEdit}
                  disabled={saving || !editingName.trim()}
                  style={{
                    padding: '10px 14px',
                    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                    border: 'none',
                    borderRadius: 12,
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontWeight: 900,
                    color: 'white',
                    boxShadow: '0 6px 16px rgba(37, 99, 235, 0.25)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                >
                  <i className="fas fa-save" />
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Popup (same style as Admin News) */}
        {categoryToDelete && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10001,
              padding: window.innerWidth <= 768 ? '10px' : '20px',
              paddingTop: window.innerWidth <= 768 ? '74px' : '20px',
              overflowY: 'auto'
            }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: window.innerWidth <= 768 ? '12px' : '16px',
                maxWidth: window.innerWidth <= 768 ? '100%' : '450px',
                width: '100%',
                position: 'relative',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Gradient bar */}
              <div style={{
                position: 'absolute',
                top: '0px',
                left: '0px',
                right: '0px',
                height: '4px',
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                borderRadius: window.innerWidth <= 768 ? '12px 12px 0 0' : '16px 16px 0 0'
              }} />

              <div style={{ padding: window.innerWidth <= 768 ? '16px' : '24px' }}>
                {/* Warning icon */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: window.innerWidth <= 768 ? '16px' : '20px'
                }}>
                  <div style={{
                    width: window.innerWidth <= 768 ? '56px' : '64px',
                    height: window.innerWidth <= 768 ? '56px' : '64px',
                    borderRadius: '50%',
                    background: '#fef2f2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '3px solid #fee2e2'
                  }}>
                    <i className="fas fa-exclamation-triangle" style={{
                      fontSize: window.innerWidth <= 768 ? '28px' : '32px',
                      color: '#ef4444'
                    }} />
                  </div>
                </div>

                <h2 style={{
                  margin: 0,
                  fontSize: window.innerWidth <= 768 ? '18px' : '20px',
                  fontWeight: 700,
                  color: '#1f2937',
                  textAlign: 'center',
                  marginBottom: '12px'
                }}>
                  Delete Category
                </h2>

                <p style={{
                  margin: 0,
                  fontSize: window.innerWidth <= 768 ? '13px' : '14px',
                  color: '#6b7280',
                  textAlign: 'center',
                  lineHeight: '1.6',
                  marginBottom: window.innerWidth <= 768 ? '20px' : '24px'
                }}>
                  Are you sure you want to delete the category <strong>"{categoryToDelete.name}"</strong>? This action is irreversible.
                </p>

                <div style={{
                  display: 'flex',
                  gap: window.innerWidth <= 768 ? '8px' : '12px',
                  flexDirection: window.innerWidth <= 768 ? 'column' : 'row'
                }}>
                  <button
                    onClick={() => setCategoryToDelete(null)}
                    disabled={saving}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      background: '#f3f4f6',
                      color: '#374151',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: 600,
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (!saving) e.target.style.background = '#e5e7eb';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#f3f4f6';
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDeleteCategory}
                    disabled={saving}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: 600,
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                      if (!saving) e.target.style.background = '#dc2626';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#ef4444';
                    }}
                  >
                    <i className="fas fa-trash" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Categories;


